import { BookOpen, Bot, ClipboardCheck, Compass, ArrowRight, Shuffle, CheckCircle2 } from "lucide-react";
import { FadeUp, Overline } from "./Reveal";
import { scrollToId } from "./Navbar";

export function Reassurance() {
  const items = [
    { icon: BookOpen, title: "Apprendre", desc: "Comprendre les valeurs, institutions et principes de la République.", color: "text-bleu bg-bleu-light" },
    { icon: Bot, title: "Être accompagné", desc: "Posez vos questions à nos assistants IA.", color: "text-violet-600 bg-violet-50" },
    { icon: ClipboardCheck, title: "Se préparer", desc: "Révisez et entraînez-vous grâce aux quiz et examens blancs.", color: "text-rouge bg-rose-50" },
    { icon: Compass, title: "Savoir quoi faire", desc: "Identifiez les démarches et prochaines étapes correspondant à votre situation.", color: "text-emeraude bg-emerald-50" },
  ];
  return (
    <section className="py-16 sm:py-20">
      <div className="container-integra grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it, i) => (
          <FadeUp key={it.title} delay={i * 0.1}>
            <div className="card-integra card-hover h-full p-6" data-testid="reassurance-pillar-item">
              <span className={`inline-flex rounded-2xl p-3 ${it.color}`}>
                <it.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 font-display text-sm font-bold uppercase tracking-wider text-slate-900">{it.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{it.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

export function Why() {
  return (
    <section id="pourquoi" className="bg-slate-50/70 py-20 sm:py-28">
      <div className="container-integra grid items-center gap-14 lg:grid-cols-2">
        <FadeUp>
          <Overline>Pourquoi INTEGRA FRANCE ?</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            Les démarches et informations peuvent être difficiles à comprendre.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Entre les démarches administratives, les formations, les documents, les échéances et la préparation à
            l'examen civique, il n'est pas toujours facile de savoir par où commencer.
          </p>
          <p className="mt-4 border-l-4 border-bleu pl-4 text-base font-semibold leading-relaxed text-bleu">
            INTEGRA FRANCE rassemble les informations et vous accompagne dans un parcours simple et personnalisé.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="relative">
            <div className="card-integra p-6">
              <div className="flex items-center gap-2 text-ambre">
                <Shuffle className="h-4 w-4" />
                <p className="text-xs font-bold uppercase tracking-widest">Avant — un parcours complexe</p>
              </div>
              <div className="mt-4 space-y-2.5">
                {["Préfecture ? ANEF ? OFII ?", "Quels documents ?", "Quelles échéances ?", "Par où commencer ?"].map((t, i) => (
                  <div
                    key={t}
                    className="w-fit rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3.5 py-2 text-xs text-slate-500"
                    style={{ marginLeft: `${[0, 28, 12, 40][i]}px`, transform: `rotate(${[-1.5, 1, -0.8, 1.4][i]}deg)` }}
                  >
                    {t}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative z-10 mx-auto -my-3 flex h-11 w-11 items-center justify-center rounded-full bg-bleu text-white shadow-lg shadow-bleu/30">
              <ArrowRight className="h-5 w-5 rotate-90" />
            </div>

            <div className="card-integra border-bleu/20 p-6 shadow-[0_20px_50px_-20px_rgba(30,63,174,0.3)]">
              <div className="flex items-center gap-2 text-emeraude">
                <CheckCircle2 className="h-4 w-4" />
                <p className="text-xs font-bold uppercase tracking-widest">Avec INTEGRA FRANCE — simple et organisé</p>
              </div>
              <div className="mt-4 space-y-2.5">
                {["1. Comprendre ma situation", "2. Me former à mon rythme", "3. Préparer mon examen", "4. Avancer dans mes démarches"].map((t) => (
                  <div key={t} className="flex items-center gap-2.5 rounded-lg bg-emerald-50/70 px-3.5 py-2.5 text-xs font-medium text-emerald-900">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emeraude" /> {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export function Pillars() {
  const cards = [
    {
      num: "01", tag: "M'informer", title: "Comprendre les étapes de mon parcours",
      desc: "Découvrez les informations utiles à votre situation et accédez aux ressources officielles.",
      cta: "En savoir plus", target: "ressources", testid: "pillar-card-informer",
    },
    {
      num: "02", tag: "Me former", title: "Comprendre la France et ses valeurs",
      desc: "Des contenus pédagogiques sur les principes et valeurs de la République, les institutions, les droits et devoirs, l'histoire, la géographie, la culture et la vie en société.",
      cta: "Découvrir les formations", target: "parcours", testid: "pillar-card-former",
    },
    {
      num: "03", tag: "Me préparer", title: "Préparer mon examen civique",
      desc: "Testez vos connaissances, identifiez vos points faibles et entraînez-vous grâce à des quiz et examens blancs.",
      cta: "Découvrir le Coach civique", target: "examen", testid: "pillar-card-preparer",
    },
    {
      num: "04", tag: "M'orienter", title: "Comprendre mes démarches",
      desc: "Posez votre question simplement à l'assistant administratif et soyez orienté vers les informations et sources officielles pertinentes.",
      cta: "Découvrir l'assistant", target: "assistant", testid: "pillar-card-orienter",
    },
  ];
  return (
    <section id="piliers" className="py-20 sm:py-28">
      <div className="container-integra">
        <FadeUp className="max-w-2xl">
          <Overline>Un assistant pour votre parcours</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Un seul espace pour mieux comprendre votre parcours en France.
          </h2>
        </FadeUp>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <FadeUp key={c.num} delay={i * 0.1} className="h-full">
              <div className="card-integra card-hover group flex h-full flex-col p-7" data-testid={c.testid}>
                <span className="font-display text-4xl font-extrabold text-bleu/15 transition-colors duration-300 group-hover:text-bleu/30">
                  {c.num}
                </span>
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.16em] text-bleu">{c.tag}</p>
                <h3 className="mt-2 font-display text-lg font-bold leading-snug text-slate-900">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{c.desc}</p>
                <a
                  href={`#${c.target}`}
                  onClick={scrollToId(c.target)}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-bleu transition-transform duration-300 group-hover:translate-x-1"
                >
                  {c.cta} <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
